import { getNameFull } from '@core/util/client';
import {
	getBusinessTemplateStorageRefByDocument,
	getDocumentHashedId,
	getMatterDocumentStorageRefByDocument,
} from '@core/util/documents';
import { getEncumbranceInstitutionShortName } from '@core/util/encumbrance';
import { getMortgageLenderName, getMortgageLenders } from '@core/util/mortgage';
import { capitalizeFirstWord, joinNames } from '@core/util/string';
import {
	Encumbrance,
	EncumbranceActionType,
	EncumbranceType,
	MortgagePriorityDescription,
} from '@models/encumbrance';
import {
	Department,
	Jurisdiction,
	JurisdictionDepartments,
} from '@models/jurisdictions';
import { Debt, DocumentContextType, MatterFileType } from '@models/matter';
import { GroupType } from '@models/documents';
import {
	MatterMortgage,
	MatterMortgagePriority,
	MatterMortgageWithAssociations,
	TransactionType,
} from '@models/mortgage';
import { LenderWithAssociations } from '@models/lender';
import { GlobalWordDocumentTemplate } from '@models/documents';
import { createSelector, Selector } from '@ngxs/store';
import { BusinessState } from '@state/business';
import { BusinessWordDocumentsState } from '@state/business/word/business-word-documents/business-word-documents.state';
import { Clients, MatterClients } from '@state/global/matter_clients';
import { MappedMortgage } from '@state/global/tags/models/mortgages.model';
import { DebtsState } from '@state/matter/debts';
import { MatterEncumbrancesState } from '@state/matter/encumbrances';
import { MatterJurisdictionSelectors } from '@state/matter/jurisdictions/jurisdictions.selectors';
import { MatterWordDocumentWithMetadata } from '@state/matter/matter-word-documents/matter-word-documents.selectors';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterMortgagesState } from '@state/matter/matter_mortgages';
import {
	DocumentsV2Selectors,
	DocumentUsageStats,
} from '../documents-v2.selectors';

export interface GlobalWordDocumentTemplateWithContext
	extends GlobalWordDocumentTemplate {
	contextType: DocumentContextType;
}

export interface ContextItem {
	referenceId: string;
	name: string;
}

export interface Contexts {
	clients: ContextItem[];
	mortgages: ContextItem[];
	encumbrances: ContextItem[];
	debts: ContextItem[];
	jurisdictions: ContextItem[];
}

export interface DrawerDocument extends MatterWordDocumentWithMetadata {
	isAdded: boolean;
	usageScore: number;
}

export class DocumentsDrawerSelectors {
	static GroupTypeToJurisdictionDepartmentMapping = {
		[GroupType.JURISDICTION_BUILDING_DEPARTMENT]:
			JurisdictionDepartments.BUILDING,
		[GroupType.JURISDICTION_CONSERVATION_AUTH_DEPARTMENT]:
			JurisdictionDepartments.CONSERVATION_AUTHORITY,
		[GroupType.JURISDICTION_FIRE_DEPARTMENT]: JurisdictionDepartments.FIRE,
		[GroupType.JURISDICTION_HYDRO_DEPARTMENT]: JurisdictionDepartments.HYDRO,
		[GroupType.JURISDICTION_TAX_DEPARTMENT]: JurisdictionDepartments.TAX,
		[GroupType.JURISDICTION_WATER_DEPARTMENT]: JurisdictionDepartments.WATER,
		[GroupType.JURISDICTION_ZONING_DEPARTMENT]: JurisdictionDepartments.ZONING,
	};

	@Selector([
		BusinessWordDocumentsState.getAllMerged,
		SelectedMatter.matterFileType,
	])
	static getGlobalTemplatesByMatterFileType(
		documents: GlobalWordDocumentTemplate[],
		fileType: MatterFileType,
	): GlobalWordDocumentTemplate[] {
		const docs = documents.filter((doc) => doc.matterType.includes(fileType));
		return docs;
	}

	@Selector([DocumentsDrawerSelectors.getGlobalTemplatesByMatterFileType])
	static getGlobalTemplateMatterGroupTypes(
		templates: GlobalWordDocumentTemplate[],
	): GroupType[] {
		const groupTypes = Array.from(
			new Set(
				templates.map((t) => t.groupType).filter((groupType) => groupType),
			),
		);
		return groupTypes;
	}

	@Selector([
		MatterClients.get_clients,
		MatterMortgagesState.getAllMortgagesWithAssociations,
		MatterEncumbrancesState.getAll,
		DebtsState.getAll,
		MatterJurisdictionSelectors.getAll,
	])
	static getAllContextsData(
		clients: Clients[],
		mortgages: MatterMortgageWithAssociations[],
		encumbrances: Encumbrance[],
		debts: Debt[],
		jurisdictions: Jurisdiction[],
	): Contexts {
		return {
			clients: clients.map((x) => ({
				referenceId: x.id,
				name: getNameFull(x),
			})),
			mortgages: mortgages.map((x) => {
				return {
					referenceId: x.id,
					name: this.getMortgageName(x),
				};
			}),
			encumbrances: encumbrances.map((x) => {
				const encumbranceName = this.getEncumbranceName(x);
				return {
					referenceId: x.id,
					name: encumbranceName,
				};
			}),
			debts: debts.map((x) => {
				return {
					referenceId: x.id,
					name: `${x.item} - ${x.payee?.name}`,
				};
			}),
			jurisdictions: jurisdictions?.map((x) => {
				return {
					referenceId: x.id,
					name: x.name,
					departments: x.departments,
				};
			}),
		};
	}

	@Selector([
		DocumentsDrawerSelectors.getVirtualizedDocuments,
		DocumentsDrawerSelectors.getMatterDocumentsWithMetadata,
	])
	static getAllVirtualAndMatterDocuments(
		virtualizedDocs: { [id: string]: MatterWordDocumentWithMetadata },
		matterDocs: { [id: string]: MatterWordDocumentWithMetadata },
	): MatterWordDocumentWithMetadata[] {
		return Object.values({
			...virtualizedDocs,
			...matterDocs,
		});
	}

	@Selector([
		DocumentsDrawerSelectors.getGlobalTemplatesByMatterFileType,
		DocumentsDrawerSelectors.getAllContextsData,
		SelectedMatter.id,
		BusinessState.businessId,
	])
	static getVirtualizedDocuments(
		templates: GlobalWordDocumentTemplate[],
		contexts: Contexts,
		matterId: string,
		businessId: string,
	): { [id: string]: MatterWordDocumentWithMetadata } {
		const { clients, mortgages, encumbrances, debts, jurisdictions } = contexts;
		const contextMap = {
			[GroupType.AFFIDAVIT]: clients,
			[GroupType.MORTGAGE]: mortgages,
			[GroupType.ENCUMBRANCE]: encumbrances,
			[GroupType.DEBT]: debts,
			[GroupType.JURISDICTION]: jurisdictions,
			[GroupType.REMAIN]: encumbrances,
		};
		const virtualizedDocs = templates.reduce((acc, template) => {
			return {
				...acc,
				...this.getInstancesOfTemplate(
					template,
					contextMap,
					matterId,
					businessId,
				),
			};
		}, {});

		// Required to generate the context config file for Word Processing Tests
		/* const virtualizedDocsTyped = virtualizedDocs as { [id: string]: MatterWordDocumentWithMetadata };
		
		const contextConfig = Object.values(virtualizedDocsTyped)
		.map((doc) => ({
			templateId: doc.document.source,
			context: doc.document.context,
		}));

		console.log("contextConfig", contextConfig); */

		return virtualizedDocs;
	}

	@Selector([DocumentsV2Selectors.getDocumentsWithStatusesAndPackages])
	static getMatterDocumentsWithMetadata(
		documents: MatterWordDocumentWithMetadata[],
	): {
		[id: string]: MatterWordDocumentWithMetadata;
	} {
		return documents.reduce((acc, doc) => {
			acc[doc.document.id] = {
				...doc,
				isGenerated: true,
			};
			return acc;
		}, {});
	}

	static getInstancesOfTemplate = (
		template,
		contextMap,
		matterId,
		businessId,
	): { [id: string]: MatterWordDocumentWithMetadata } => {
		// Handle jurisdiction department types
		if (template.groupType in this.GroupTypeToJurisdictionDepartmentMapping) {
			const result = {};
			contextMap.jurisdiction?.forEach((juris: Jurisdiction) => {
				if (!juris?.departments) {
					return;
				}
				// Getting department info
				const departmentType =
					this.GroupTypeToJurisdictionDepartmentMapping[template.groupType];
				const departments = Object.values(juris.departments) as Department[];
				const department = departments?.find((x) => x.type === departmentType);

				if (!department) {
					return;
				}

				const instance = this.getInstanceOfTemplate(
					template,
					{
						...juris,
						department,
					},
					matterId,
					businessId,
				);
				result[instance.document.id] = instance;
			});
			return result;
		}

		// Handle regular types
		if (!contextMap[template.groupType]) {
			const instance = this.getInstanceOfTemplate(
				template,
				null,
				matterId,
				businessId,
			);
			return { [instance.document.id]: instance };
		} else {
			const result = {};
			contextMap[template.groupType].forEach((contextItem) => {
				const instance = this.getInstanceOfTemplate(
					template,
					contextItem,
					matterId,
					businessId,
				);
				result[instance.document.id] = instance;
			});
			return result;
		}
	};

	static getInstanceOfTemplate = (
		template,
		contextItem,
		matterId,
		businessId,
	): MatterWordDocumentWithMetadata => {
		const id = getDocumentHashedId(matterId, template.id, contextItem, true);

		let context = null;
		if (contextItem) {
			// Handle jurisdiction department types
			if (template.groupType in this.GroupTypeToJurisdictionDepartmentMapping) {
				context = {
					jurisdictions: [
						{
							referenceId: contextItem.referenceId,
							name: contextItem.name,
						},
					],
					departments: [
						{
							referenceId: contextItem.department?.id,
							name: contextItem.department?.typeName,
						},
					],
				};
				if (contextItem.department?.complianceRequests) {
					context.complianceRequests = Object.keys(
						contextItem.department?.complianceRequests,
					).map((key) => {
						return {
							referenceId: key,
						};
					});
				}
			} else if (template.groupType === GroupType.AFFIDAVIT) {
				context = {
					clients: [
						{ referenceId: contextItem.referenceId, name: contextItem.name },
					],
				};
			} else {
				// Handle regular context types
				context = {
					[template.groupType.toLowerCase() + 's']: [
						{
							referenceId: contextItem.referenceId,
							name: contextItem.name,
						},
					],
				};
			}
		}

		return {
			id,
			isGenerated: false,
			document: {
				...template,
				id,
				sourceStorageRef: getBusinessTemplateStorageRefByDocument(template),
				destinationStorageRef: getMatterDocumentStorageRefByDocument(
					businessId,
					matterId,
					{ ...template, id },
				),
				templateId: template.id,
				context,
			},
		};
	};

	static getEncumbranceName = (encumbrance: Encumbrance): string => {
		const description = getEncumbranceInstitutionShortName(encumbrance);
		let prefix = '';
		let suffix = '';

		switch (encumbrance.type) {
			case EncumbranceType.LIEN:
				prefix = 'Lien';
				break;
			case EncumbranceType.MORTGAGE:
				switch (encumbrance.action) {
					case EncumbranceActionType.ASSUME:
						prefix = 'Assume';
						break;
					case EncumbranceActionType.BRIDGE:
						prefix = 'Bridge Loan';
						break;
					default:
						prefix = `${MortgagePriorityDescription[encumbrance.priority]} Mortgage`;
						break;
				}
				break;
			case EncumbranceType.NOTICE_OF_SECURITY_INTEREST:
				prefix = 'Security Interest';
				break;
		}

		if (encumbrance.action === EncumbranceActionType.REMAIN) {
			suffix = ' (Remain)';
		}

		return [prefix, description]
			.filter((chunk) => !!chunk)
			.join(' - ')
			.concat(suffix)
			.trim();
	};

	static getMortgageName = (
		mortgage: MatterMortgageWithAssociations,
		useShortName = false,
	): string => {
		const lenders: LenderWithAssociations[] = getMortgageLenders(
			mortgage,
		) as LenderWithAssociations[];
		let pre = '';
		const lenderName =
			lenders?.length > 0
				? joinNames(
						lenders.map((lender) => getMortgageLenderName(lender, true)),
					)
				: '';
		let mortgageName = '';
		if (mortgage?.transactionType === TransactionType.BRIDGE) {
			pre = 'Bridge';
			mortgageName = `${pre} - ${lenderName}`;
		} else if (mortgage?.priority && lenderName) {
			pre = useShortName
				? MortgagePriorityDescription[mortgage?.priority]
				: this.getMortgageTypePriority(mortgage);
			mortgageName = `${pre} - ${lenderName}`;
		} else if (lenderName) {
			mortgageName = lenderName;
		} else if (mortgage?.priority) {
			mortgageName = useShortName
				? MortgagePriorityDescription[mortgage?.priority]
				: this.getMortgageTypePriority(mortgage);
		}

		mortgageName = capitalizeFirstWord(mortgageName);

		return mortgageName;
	};

	static getMortgageTypePriority = (
		mortgage: MappedMortgage | MatterMortgage,
	) => {
		let mortgageType = '';

		let MortgagePriority = null;

		if (mortgage?.priority) {
			MortgagePriority =
				MortgagePriorityDescription[
					mortgage?.priority as MatterMortgagePriority
				];
		}

		const transactionType: TransactionType =
			(mortgage as MappedMortgage)?.transactionType?.defaultValue ??
			(mortgage as MatterMortgage)?.transactionType;

		if (transactionType === TransactionType.ARRANGED) {
			mortgageType = `${MortgagePriority} mortgage`;
		} else if (transactionType === TransactionType.BRIDGE) {
			mortgageType = 'bridge';
		} else if (transactionType === TransactionType.VTB) {
			mortgageType = `${MortgagePriority} VTB`;
		}

		return mortgageType;
	};

	static getDocumentsDrawerData = createSelector(
		[
			DocumentsV2Selectors.getDocumentUsageStats,
			DocumentsDrawerSelectors.getAllVirtualAndMatterDocuments,
			SelectedMatter.matterFileType,
		],
		(
			documentUsage: DocumentUsageStats,
			allDocuments: MatterWordDocumentWithMetadata[],
			fileType: MatterFileType,
		) => {
			return (packageId?: string) => {
				const documentsWithAddedStatusAndScore = allDocuments.map((doc) => ({
					...doc,
					isAdded: packageId
						? doc.includedInPackages?.some((pkg) => pkg.id === packageId)
						: doc.isGenerated,
					usageScore:
						documentUsage[doc.document.source]?.usageByType[fileType]
							?.weightedScore || 0,
				}));

				const topDocuments = documentsWithAddedStatusAndScore.sort((a, b) => {
					const dateA = a.document.updatedAt || a.document.createdAt || 0;
					const dateB = b.document.updatedAt || b.document.createdAt || 0;
					return dateB - dateA;
				});

				const topAddedDocuments: DrawerDocument[] = [];
				const topAvailableDocuments: DrawerDocument[] = [];

				const drawerDocMap = new Map<string, DrawerDocument[]>();
				for (const doc of topDocuments) {
					drawerDocMap.set(doc.document.source, [
						...(drawerDocMap.get(doc.document.source) || []),
						doc,
					]);
				}

				for (const drawerDocsBySource of drawerDocMap.values()) {
					for (const drawerDoc of drawerDocsBySource) {
						if (drawerDoc.usageScore > 0) {
							if (drawerDoc.isAdded) {
								topAddedDocuments.push(drawerDoc);
							} else if (topAvailableDocuments.length < 15) {
								topAvailableDocuments.push(drawerDoc);
							}
						}
					}
				}

				return {
					allDocuments: documentsWithAddedStatusAndScore,
					topAddedDocuments,
					topAvailableDocuments,
				};
			};
		},
	);
}
